import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import PageHeader from '../../components/PageHeader.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { workspaceApi } from '../../api/workspace.js';
import { apiError } from '../../api/client.js';

export default function SettingsPage() {
  const { workspace, setWorkspace } = useAuth();
  const [name, setName] = useState('');
  const [provider, setProvider] = useState('anthropic');
  const [openaiKey, setOpenaiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [hasOpenai, setHasOpenai] = useState(false);
  const [hasAnthropic, setHasAnthropic] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!workspace) return;
    workspaceApi
      .show(workspace.id)
      .then((data) => {
        setName(data.workspace.name);
        setProvider(data.ai.provider);
        setHasOpenai(data.ai.has_openai_key);
        setHasAnthropic(data.ai.has_anthropic_key);
      })
      .catch((err) => toast.error(apiError(err)));
  }, [workspace?.id]);

  async function save() {
    if (!workspace) return;
    setBusy(true);
    try {
      const payload = { name, ai_provider: provider };
      if (openaiKey) payload.openai_api_key = openaiKey;
      if (anthropicKey) payload.anthropic_api_key = anthropicKey;
      const data = await workspaceApi.update(workspace.id, payload);
      setWorkspace(data.workspace);
      setHasOpenai(data.ai.has_openai_key);
      setHasAnthropic(data.ai.has_anthropic_key);
      setOpenaiKey('');
      setAnthropicKey('');
      toast.success('Settings saved');
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Workspace settings"
        description="Profile, AI provider, and API keys for this workspace. Keys are encrypted at rest."
      />
      <div className="mx-auto max-w-2xl space-y-6 px-8 py-8">
        <section className="card p-6">
          <h2 className="mb-4 text-base font-semibold">Workspace</h2>
          <div>
            <label className="label">Name</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
        </section>

        <section className="card p-6">
          <h2 className="mb-1 text-base font-semibold">AI provider</h2>
          <p className="mb-4 text-sm text-ink-500">
            Choose which provider powers AI generation. Bring your own key for either.
          </p>
          <div className="mb-5 grid grid-cols-2 gap-3">
            <ProviderCard
              label="Anthropic"
              sub="Claude Sonnet 4.6"
              active={provider === 'anthropic'}
              configured={hasAnthropic}
              onClick={() => setProvider('anthropic')}
            />
            <ProviderCard
              label="OpenAI"
              sub="GPT-4o mini"
              active={provider === 'openai'}
              configured={hasOpenai}
              onClick={() => setProvider('openai')}
            />
          </div>

          <div className="space-y-4">
            <div>
              <label className="label">
                Anthropic API key {hasAnthropic && <span className="text-emerald-600">· saved</span>}
              </label>
              <input
                type="password"
                className="input"
                value={anthropicKey}
                onChange={(e) => setAnthropicKey(e.target.value)}
                placeholder={hasAnthropic ? '•••••••• (leave blank to keep)' : 'sk-ant-…'}
              />
            </div>
            <div>
              <label className="label">
                OpenAI API key {hasOpenai && <span className="text-emerald-600">· saved</span>}
              </label>
              <input
                type="password"
                className="input"
                value={openaiKey}
                onChange={(e) => setOpenaiKey(e.target.value)}
                placeholder={hasOpenai ? '•••••••• (leave blank to keep)' : 'sk-…'}
              />
            </div>
          </div>
        </section>

        <div className="flex justify-end">
          <button onClick={save} className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </>
  );
}

function ProviderCard({ label, sub, active, configured, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-xl border p-4 text-left transition ' +
        (active
          ? 'border-brand-500 bg-brand-50 dark:bg-ink-800'
          : 'border-ink-200 hover:border-ink-300 dark:border-ink-800 dark:hover:border-ink-700')
      }
    >
      <div className="flex items-center justify-between">
        <div className="font-semibold">{label}</div>
        {configured && (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">
            Key set
          </span>
        )}
      </div>
      <div className="mt-1 text-xs text-ink-500">{sub}</div>
    </button>
  );
}
